import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CacheContext = createContext();

// Cache configuration
const CACHE_CONFIG = {
    expenses: { ttl: 5 * 60 * 1000 }, // 5 minutes
    portfolio: { ttl: 2 * 60 * 1000 }, // 2 minutes (more frequent for real-time prices)
    reminders: { ttl: 10 * 60 * 1000 }, // 10 minutes
    income: { ttl: 5 * 60 * 1000 }, // 5 minutes
    news: { ttl: 15 * 60 * 1000 }, // 15 minutes
    loans: { ttl: 30 * 60 * 1000 }, // 30 minutes
    investments: { ttl: 5 * 60 * 1000 }, // 5 minutes
    stockPrices: { ttl: 1 * 60 * 1000 }, // 1 minute for stock prices
    cryptoPrices: { ttl: 1 * 60 * 1000 }, // 1 minute for crypto
    mutualFunds: { ttl: 5 * 60 * 1000 }, // 5 minutes
    benchmarkRate: { ttl: 60 * 60 * 1000 }, // 1 hour
    mortgageRates: { ttl: 60 * 60 * 1000 }, // 1 hour
    savingsSuggestions: { ttl: 30 * 60 * 1000 }, // 30 minutes
};

export const CacheProvider = ({ children }) => {
    const [cache, updateCache] = useState(() => {
        // Initialize from localStorage if available
        try {
            const stored = localStorage.getItem('finassure_cache');
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    });

    // Persist cache to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem('finassure_cache', JSON.stringify(cache));
        } catch (error) {
            console.warn('Failed to persist cache:', error);
        }
    }, [cache]);

    // Check if cached data is still valid
    const isCacheValid = useCallback((key) => {
        const cached = cache[key];
        if (!cached) return false;

        const config = CACHE_CONFIG[key.split(':')[0]]; // Get config for the data type
        if (!config) return false;

        const age = Date.now() - cached.timestamp;
        return age < config.ttl;
    }, [cache]);

    // Get data from cache
    const getCache = useCallback((key) => {
        if (isCacheValid(key)) {
            console.log(`[Cache HIT] ${key}`);
            return cache[key].data;
        }
        console.log(`[Cache MISS] ${key}`);
        return null;
    }, [cache, isCacheValid]);

    // Set data in cache
    const setCache = useCallback((key, data) => {
        console.log(`[Cache SET] ${key}`);
        updateCache(prev => ({
            ...prev,
            [key]: {
                data,
                timestamp: Date.now()
            }
        }));
    }, []);

    // Clear specific cache entry
    const clearCache = useCallback((key) => {
        console.log(`[Cache CLEAR] ${key}`);
        updateCache(prev => {
            const newCache = { ...prev };
            delete newCache[key];
            return newCache;
        });
    }, []);

    // Clear all cache
    const clearAllCache = useCallback(() => {
        console.log('[Cache CLEAR ALL]');
        updateCache({});
        localStorage.removeItem('finassure_cache');
    }, []);

    // Clear cache by pattern (e.g., all stock prices)
    const clearCacheByPattern = useCallback((pattern) => {
        console.log(`[Cache CLEAR PATTERN] ${pattern}`);
        updateCache(prev => {
            const newCache = { ...prev };
            Object.keys(newCache).forEach(key => {
                if (key.startsWith(pattern)) {
                    delete newCache[key];
                }
            });
            return newCache;
        });
    }, []);

    // Invalidate cache for a specific data type
    const invalidateCache = useCallback((dataType) => {
        clearCacheByPattern(dataType);
    }, [clearCacheByPattern]);

    const value = {
        getCache,
        setCache,
        clearCache,
        clearAllCache,
        clearCacheByPattern,
        invalidateCache,
        isCacheValid
    };

    return (
        <CacheContext.Provider value={value}>
            {children}
        </CacheContext.Provider>
    );
};

export const useCache = () => {
    const context = useContext(CacheContext);
    if (!context) {
        throw new Error('useCache must be used within a CacheProvider');
    }
    return context;
};
